import { X } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../lib/cn.js';

/**
 * A word list, edited as chips rather than as comma-separated text.
 *
 * «Ord vi ikke bruker» is the field most likely to be skimmed rather than read,
 * so each entry has to be visible as its own thing — a wall of commas hides the
 * one term someone came to remove (dokumentasjon/07-utviklingsplan.md 2.1.2).
 */
export function TagInput({
  value,
  onChange,
  placeholder,
  tone = 'neutral',
  id,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  tone?: 'neutral' | 'danger';
  id?: string;
}) {
  const [draft, setDraft] = useState('');

  const add = (raw: string) => {
    // Commas and semicolons are how people paste a list in; treat them as breaks.
    const words = raw
      .split(/[,;\n]/)
      .map((w) => w.trim())
      .filter((w) => w.length > 0 && !value.includes(w));
    if (words.length > 0) onChange([...value, ...words]);
    setDraft('');
  };

  return (
    <div className="border-hairline bg-base/40 flex flex-wrap items-center gap-1.5 rounded-lg border p-2">
      {value.map((word) => (
        <span
          key={word}
          className={cn(
            'inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px]',
            tone === 'danger' ? 'bg-danger/12 text-danger' : 'bg-raised text-ink',
          )}
        >
          {word}
          <button
            type="button"
            onClick={() => onChange(value.filter((w) => w !== word))}
            aria-label={`Fjern ${word}`}
            className="opacity-60 transition hover:opacity-100"
          >
            <X size={11} aria-hidden />
          </button>
        </span>
      ))}

      <input
        id={id}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add(draft);
          }
          // Backspace on an empty field removes the last chip, as people expect.
          if (e.key === 'Backspace' && draft === '' && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => add(draft)}
        placeholder={value.length === 0 ? placeholder : ''}
        className="text-ink placeholder:text-ink-soft/50 min-w-[140px] flex-1 bg-transparent px-1 py-1 text-xs focus:outline-none"
      />
    </div>
  );
}
